import api from './api';
import { shouldConvertToPNG, convertImageToPNG } from '../utils/imageConverter';

export interface UploadedFile {
  filename: string;
  originalName: string;
  url: string;
  size: number;
  contentType: string;
}

export interface UploadResponse {
  success: boolean;
  message: string;
  data: UploadedFile;
}

export interface MultipleUploadResponse {
  success: boolean;
  message: string;
  data: UploadedFile[];
}

export interface FileListResponse {
  success: boolean;
  message: string;
  data: {
    files: UploadedFile[];
    count: number;
    folder: string;
  };
}

export interface StorageStatusResponse {
  success: boolean;
  data: {
    storageAvailable: boolean;
    bucketName: string;
    projectId: string;
    maxFileSize: string;
    allowedImageTypes: string[];
    allowedVideoTypes: string[];
    allowedDocumentTypes: string[];
  };
}

class UploadService {
  // 단일 이미지 업로드 (자동 PNG 변환 포함)
  async uploadSingleImage(file: File): Promise<UploadResponse> {
    // JPG/JPEG/PNG가 아닌 이미지는 PNG로 자동 변환
    let fileToUpload = file;
    if (shouldConvertToPNG(file)) {
      try {
        fileToUpload = await convertImageToPNG(file);
      } catch (error) {
        console.error('이미지 PNG 변환 실패:', error);
        // 변환 실패 시 원본 파일로 업로드 시도
      }
    }

    const formData = new FormData();
    formData.append('image', fileToUpload);

    const response = await api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // 다중 이미지 업로드 (자동 PNG 변환 포함)
  async uploadMultipleImages(files: File[]): Promise<MultipleUploadResponse> {
    // JPG/JPEG/PNG가 아닌 이미지는 PNG로 자동 변환
    const filesToUpload = await Promise.all(
      files.map(async (file) => {
        if (shouldConvertToPNG(file)) {
          try {
            return await convertImageToPNG(file);
          } catch (error) {
            console.error(`이미지 PNG 변환 실패: ${file.name}`, error);
            // 변환 실패 시 원본 파일 반환
            return file;
          }
        }
        return file;
      })
    );

    const formData = new FormData();
    filesToUpload.forEach(file => {
      formData.append('images', file);
    });

    const response = await api.post('/upload/images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // 파일 삭제
  async deleteFile(filename: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/upload/files/${encodeURIComponent(filename)}`);
    return response.data;
  }

  // 업로드된 파일 목록 조회
  async getUploadedFiles(folder = 'restaurant-guides', limit = 100): Promise<FileListResponse> {
    const response = await api.get('/upload/files', {
      params: { folder, limit }
    });
    return response.data;
  }

  // Storage 상태 확인
  async getStorageStatus(): Promise<StorageStatusResponse> {
    const response = await api.get('/upload/status');
    return response.data;
  }

  // 이미지 URL로부터 업로드
  async uploadImageFromUrl(imageUrl: string, title?: string): Promise<UploadResponse> {
    const response = await api.post('/upload/image-from-url', {
      imageUrl,
      title
    });
    return response.data;
  }

  // 파일 크기를 읽기 좋은 형태로 변환
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // 이미지 파일인지 확인
  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  // 파일 타입이 허용되는지 확인
  isAllowedImageType(file: File): boolean {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    return allowedTypes.includes(file.type);
  }

  // 파일 크기가 허용 범위인지 확인 (기본 50MB)
  isValidFileSize(file: File, maxSize = 50 * 1024 * 1024): boolean {
    return file.size <= maxSize;
  }

  // 파일 유효성 검사
  validateFile(file: File): { valid: boolean; error?: string } {
    if (!this.isImageFile(file)) {
      return { valid: false, error: '이미지 파일만 업로드할 수 있습니다.' };
    }

    if (!this.isAllowedImageType(file)) {
      return { valid: false, error: '지원하지 않는 이미지 형식입니다. (jpg, jpeg, png, gif, webp, svg 지원)' };
    }

    if (!this.isValidFileSize(file)) {
      return { valid: false, error: '파일 크기는 50MB를 초과할 수 없습니다.' };
    }

    return { valid: true };
  }
}

export const uploadService = new UploadService();
export default uploadService;